import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ShoppingCart } from 'shared/icons';
import {
    CHARTABLE_COLS,
    COL_DEFS,
    GetTagBadgeColor as getTagBadgeColor,
    IsColumnMasked as isColumnMasked,
    IsNumericColumn as isNumericColumn,
} from '../constants/columns';
import { EvaluateExpression, FormatExpressionValue } from '../expressionEngine';
import { GetCellValue as getCellValue } from '../pipeline/derive/CellValue';
import { useCellSelection } from '../useCellSelection';
import {
    DailySliceForCol as dailySliceForCol,
    GetColumnDateRangeText as getColumnDateRangeText,
} from '../utils/columnHelpers';
import { SecureMlImageUrl as secureMlImageUrl } from '../../../utils/ml';
import { FormatDecimal1, FormatInt, FormatPercent, FormatPrice } from '../utils/numberUtils';
import {
    AUTO_WIDTH_MAX_PX,
    CELL_FONT,
    ComputeAutofitForContent,
    ComputeAutofitForHeader,
    HEADER_FONT,
    ResolveColumnWidth,
    useColumnWidths,
} from './columnResize';
import { MlbListingsPopover } from './MlbListingsPopover';
import { Spark, TBadge } from './primitives';
import { ResizeHandle } from './ResizeHandle';
import { SelectionStatsBar } from './SelectionStatsBar';
import { useTokens } from './tokens';

/**
 * Mapeia nome da tag → variante do TBadge. Idêntico à lógica de `tagColor` no
 * DetailPanel (ambos derivam a cor pelo nome).
 *   - `sem estoque`   → danger (vermelho, mais grave)
 *   - `baixo/indisp`  → warn   (laranja)
 *   - `sem venda`     → muted  (cinza, informativo)
 *   - `alta`          → ok     (verde, positivo)
 *   - resto           → accent (azul, neutro)
 */
// Vermelho usado pra qualquer número ZERO em colunas numéricas (estoque,
// vendas, etc) — sinaliza ausência sem precisar piscar bordas/badges. SE !== 0,
// usa cor padrão do tema (T.ink). Aplicado em renderCell de toda numérica.
const ZERO_RED = 'rgb(255, 120, 120)';
function numericCellColor(value, T) {
    return Number(value || 0) === 0 ? ZERO_RED : T.ink;
}

/**
 * Sub-texto padronizado dentro de uma célula: data DD/MM em bestDay, "+N tags"
 * em tags, "+N" em MLBs, número à direita do sparkline, etc. Mesma fonte
 * (mono), mesmo tamanho (11px), mesma cor (T.muted). Antes cada lugar usava
 * variações arbitrárias (10/11/12px, mono/sans, subtle/muted).
 */
function SubText({ T, children, ml = 4 }) {
    return (
        <span
            style={{
                fontFamily: T.mono,
                fontSize: 11,
                color: T.muted,
                marginLeft: ml,
                whiteSpace: 'nowrap',
            }}
        >
            {children}
        </span>
    );
}

/**
 * Célula NUMÉRICA padrão da DataTable. Toda coluna numérica (estoque, vendas,
 * preço, custo, margem, custom-cols numéricas) usa esse componente. Garante:
 *   - Mesma fonte (mono 12px) em todos os lugares
 *   - Mesma regra de cor: vermelho quando 0, T.ink caso contrário
 *   - Suffix opcional renderizado como <SubText> (mesma fonte 11/muted)
 *   - fontWeight opcional pra valores "warning" (low stock)
 *
 * Antes cada branch do renderCell duplicava `fontFamily: T.mono, fontSize: 12,
 * color: numericCellColor(...)` com pequenas variações arbitrárias.
 */
function NumericCell({ T, value, suffix = null, weight = 400, title, colorValue }) {
    // Cor é decidida por `colorValue` quando informado (ex: "82d" mostra texto
    // com unidade mas decide cor pelo número 82). Default: usar o próprio
    // value (pra inteiros simples como bling/mlAvail/sales).
    const valForColor = colorValue !== undefined ? colorValue : value;
    return (
        <span
            title={title}
            style={{
                fontFamily: T.mono,
                fontSize: 12,
                color: numericCellColor(valForColor, T),
                fontWeight: weight,
                whiteSpace: 'nowrap',
            }}
        >
            {value ?? 0}
            {suffix !== null && suffix !== undefined && <SubText T={T}>{suffix}</SubText>}
        </span>
    );
}

/**
 * Célula TEXTUAL padrão. Pra nome do produto (uppercase + ellipsis) e tipos
 * similares. SKU/MLB usam o próprio render (mono).
 */
function TextCell({ T, value, uppercase = false }) {
    return (
        <span
            title={value}
            style={{
                fontFamily: T.sans,
                fontSize: 13,
                fontWeight: 500,
                color: T.ink,
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                textTransform: uppercase ? 'uppercase' : 'none',
            }}
        >
            {value}
        </span>
    );
}

/**
 * Input numérico inline da coluna `select`. Visual MINIMALISTA — não parece
 * um input, parece o próprio conteúdo da célula: sem borda, sem background,
 * só o número (ou placeholder `—`) na fonte mono.
 *
 *   - State local pra acomodar digitação fluida (sem flicker entre keystrokes).
 *   - Sync explícito quando `qty` externo muda (cart modal alterou em outro
 *     lugar, limpar carrinho, etc) — usa ref pra comparar.
 *   - Cada onChange persiste IMEDIATAMENTE no carrinho — sem Enter, sem blur.
 *   - Vazio/zero = remove do carrinho.
 *   - Quando tem valor, fica em accent + bold pra destacar visualmente sem
 *     poluir com borda.
 */
const CART_INPUT_MAX = 999999;

function QtyInput({ row, qty, onSetCartQty, T }) {
    const [value, setValue] = useState(() => (Number(qty) > 0 ? String(qty) : ''));
    useEffect(() => {
        setValue(Number(qty) > 0 ? String(qty) : '');
    }, [qty]);
    const handleChange = (e) => {
        const raw = e.target.value;
        if (raw !== '' && !/^\d+$/.test(raw)) {
            return;
        }
        // Clamp digitado a CART_INPUT_MAX. Antes só clampava no incremento das
        // setas do `type=number` (e o input nem era number); user reportou que
        // dava pra digitar 200000 sem trava.
        let next = raw;
        if (next !== '' && Number(next) > CART_INPUT_MAX) {
            next = String(CART_INPUT_MAX);
        }
        setValue(next);
        const num = next === '' ? 0 : Number(next);
        onSetCartQty?.(row, num);
    };
    const handleKeyDown = (e) => {
        e.stopPropagation();
        // Tab: pular pro próximo QtyInput (próxima row) sem focar a row-button
        // intermediária. Sem isso o foco "some" e precisa de mais um Tab.
        if (e.key === 'Tab' && !e.shiftKey) {
            const inputs = Array.from(document.querySelectorAll('input[data-cart-qty]'));
            const idx = inputs.indexOf(e.currentTarget);
            const next = inputs[idx + 1];
            if (next) {
                e.preventDefault();
                next.focus();
                next.select?.();
            }
        } else if (e.key === 'Tab' && e.shiftKey) {
            const inputs = Array.from(document.querySelectorAll('input[data-cart-qty]'));
            const idx = inputs.indexOf(e.currentTarget);
            const prev = inputs[idx - 1];
            if (prev) {
                e.preventDefault();
                prev.focus();
                prev.select?.();
            }
        }
    };
    const hasValue = value !== '';
    return (
        <input
            type="text"
            inputMode="numeric"
            value={value}
            onChange={handleChange}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={handleKeyDown}
            data-cart-qty="1"
            aria-label={`Quantidade no carrinho para ${row.sku || row.mlb || ''}`}
            style={{
                width: '100%',
                height: '100%',
                textAlign: 'center',
                border: 'none',
                background: 'transparent',
                color: hasValue ? T.accent : T.subtle,
                fontFamily: T.mono,
                fontSize: 12,
                fontWeight: hasValue ? 600 : 400,
                outline: 'none',
                padding: 0,
                cursor: 'text',
            }}
        />
    );
}

/**
 * Barra horizontal compacta pra render alternativo de colunas agregadas
 * (sales7, sales30, week1..4, month1..3). Valor mostrado em mono à direita;
 * a barra preenche proporcionalmente ao máximo da coluna no conjunto visível.
 *
 * `max=0` cai pra placeholder "—" pra evitar divisão por zero.
 */
function ChartBar({ value, max, T }) {
    const v = Number(value || 0);
    const m = Number(max || 0);
    if (m <= 0) {
        return <span style={{ fontFamily: T.mono, fontSize: 12, color: T.subtle }}>—</span>;
    }
    const pct = Math.max(0, Math.min(100, (v / m) * 100));
    const isZero = v <= 0;
    return (
        <div
            style={{
                position: 'relative',
                width: '100%',
                height: 16,
                background: T.bgSoft,
                borderRadius: 2,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                padding: '0 4px',
            }}
        >
            <div
                aria-hidden="true"
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: `${pct}%`,
                    background: T.accentBg,
                    borderRight: pct > 0 && pct < 100 ? `1px solid ${T.accent}` : 'none',
                }}
            />
            <span
                style={{
                    position: 'relative',
                    fontFamily: T.mono,
                    fontSize: 11,
                    color: isZero ? T.subtle : T.ink,
                    fontWeight: isZero ? 400 : 500,
                }}
            >
                {v}
            </span>
        </div>
    );
}

function renderCell(row, col, T, customColDefs, periodDays, cartBySku, onSetCartQty, columnViews, colMax) {
    // Período menor que o requisito da coluna → célula não-confiável → "—" e sem render especial.
    if (col !== 'select' && isColumnMasked(col, periodDays)) {
        return <span style={{ color: T.subtle, fontSize: 12 }}>—</span>;
    }

    // Bar mode tem prioridade — sobrescreve qualquer render específico de
    // coluna abaixo. Pra QUALQUER coluna numérica (built-in ou custom) com
    // view='bar', renderiza ChartBar. Sem isso, colunas tipo `forecast`/
    // `stockTotal`/`coverage` ignoravam o toggle silenciosamente.
    if (col !== 'select' && columnViews?.[col] === 'bar') {
        const customDef = customColDefs?.get(col) || null;
        if (isNumericColumn(col, customDef)) {
            // Custom cols já têm valor pré-computado em `row[col]` via
            // useAugmentedRows (número cru, não formatado). Built-in cols
            // resolvem via getCellValue (pode retornar string formatada
            // tipo "R$ 12,34" — toNumericValue strip prefixos).
            const rawValue = customDef ? row[col] : getCellValue(row, col);
            const numericValue = toNumericValue(rawValue);
            if (numericValue !== null) {
                return <ChartBar value={numericValue} max={colMax?.get(col) || 0} T={T} />;
            }
        }
    }

    // Custom expression column
    if (customColDefs?.has(col)) {
        const def = customColDefs.get(col);
        const raw = row[col]; // pre-computed by DataTable
        const isError = raw instanceof Error;
        // Zero em vermelho pra custom cols numéricas também — só pega valor
        // bruto pré-formatado (números), não strings ("OK"/"BAIXO"/etc).
        const isNumericFormat = def.format === 'number' || def.format === 'percentage' || def.format === 'currency';
        const color = isError
            ? T.danger
            : isNumericFormat && typeof raw === 'number'
              ? numericCellColor(raw, T)
              : T.ink;
        return (
            <span style={{ fontFamily: T.mono, fontSize: 12, color }} title={isError ? raw.message : undefined}>
                {isError ? 'ERR' : FormatExpressionValue(raw, def.format)}
            </span>
        );
    }

    // ctx essencial: avgPeriod usa periodDays do ctx (sem ele cai pra 90d
    // e fica > bestDay quebrando matematicamente). customColDefs vai como
    // customColumnsById mas evalCustomCell precisa do prefix limpo — custom
    // cols já são interceptadas em renderCell ANTES desse raw, então o
    // ctx aqui só precisa periodDays.
    const raw = getCellValue(row, col, { periodDays });

    if (col === 'select') {
        // Lookup direto no Map<sku, qty> propagado pelo index.jsx. Se a função
        // de update não veio (preset sem permissão de OC), renderiza placeholder.
        const sku = String(row.sku || '').toUpperCase();
        const qty = cartBySku?.get(sku)?.qty || 0;
        if (!onSetCartQty) {
            return <span style={{ color: T.subtle, fontSize: 11 }}>—</span>;
        }
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <QtyInput row={row} qty={qty} onSetCartQty={onSetCartQty} T={T} />
            </div>
        );
    }

    if (col === 'thumb') {
        // Foto do 1º anúncio do SKU (ml_listings.thumbnail). 28px cabe na row
        // de 36/40px; sem foto → traço. Clique abre o anúncio quando há link.
        const src = secureMlImageUrl(row.thumbnail);
        if (!src) {
            return <span style={{ color: T.subtle }}>—</span>;
        }
        const permalink = Array.isArray(row.mlbListings) ? row.mlbListings[0]?.permalink : null;
        const img = (
            <img
                src={src}
                alt=""
                loading="lazy"
                width={28}
                height={28}
                style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 3, display: 'block', background: T.bgMid }}
            />
        );
        return permalink ? (
            <a href={permalink} target="_blank" rel="noopener noreferrer" title="Abrir anúncio" style={{ display: 'inline-block' }}>
                {img}
            </a>
        ) : (
            img
        );
    }

    if (col === 'sku') {
        // Modo inventory: row.skus lista TODOS os SKUs do inventory; mostra o
        // primário + "+N" com tooltip (mesmo padrão da coluna MLB).
        const skusList = Array.isArray(row.skus) && row.skus.length > 0 ? row.skus : raw ? [raw] : [];
        const extra = skusList.length - 1;
        return (
            <span
                title={extra > 0 ? skusList.join(', ') : undefined}
                style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, whiteSpace: 'nowrap' }}
            >
                {skusList[0] ?? raw}
                {extra > 0 && <SubText T={T}>+{extra}</SubText>}
            </span>
        );
    }

    if (col === 'inventoryId') {
        // SKU pode ter N inventories no Full (row.inventoryIds); modo inventory
        // tem exatamente 1. Mesmo render "+N" do MLB.
        const list = Array.isArray(row.inventoryIds) && row.inventoryIds.length > 0 ? row.inventoryIds : raw ? [raw] : [];
        if (list.length === 0) {
            return <span style={{ color: T.subtle }}>—</span>;
        }
        const extra = list.length - 1;
        return (
            <span
                title={extra > 0 ? list.join(', ') : undefined}
                style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, whiteSpace: 'nowrap' }}
            >
                {list[0]}
                {extra > 0 && <SubText T={T}>+{extra}</SubText>}
            </span>
        );
    }

    if (col === 'mlb') {
        // Preset por SKU pode ter múltiplos MLBs no mesmo SKU (ex: AWR1-110X74
        // com 3 MLBs). row.mlbs é a lista completa, row.mlb é o primeiro
        // (compat). Mostra "MLB1234 +2" quando tem mais de 1; tooltip lista
        // todos. Preset MLB-mode tem só 1 (cada row = 1 MLB).
        const mlbsList = Array.isArray(row.mlbs) ? row.mlbs : raw ? [raw] : [];
        if (mlbsList.length === 0) {
            return <span style={{ color: T.subtle }}>—</span>;
        }
        const first = mlbsList[0];
        const extra = mlbsList.length - 1;
        return (
            <span
                title={mlbsList.length > 1 ? mlbsList.join(', ') : undefined}
                style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, whiteSpace: 'nowrap' }}
            >
                {first}
                {extra > 0 && <SubText T={T}>+{extra}</SubText>}
            </span>
        );
    }

    if (col === 'name' || col === 'brand') {
        // UPPERCASE forçado no display via TextCell padronizado.
        return <TextCell T={T} value={raw} uppercase />;
    }

    if (col === 'mlbTitle') {
        // Título do anúncio ML — diferente do nome cadastro Bling. Em MLB mode
        // mostra direto com link. Em SKU mode mostra primeiro + popover
        // "+N anúncios" pra ver todos com links individuais.
        const title = row.mlbTitle || null;
        const listings = Array.isArray(row.mlbListings) ? row.mlbListings : [];
        const first = listings[0];
        const extra = Math.max(0, listings.length - 1);
        if (!title && listings.length === 0) {
            return <span style={{ color: T.subtle }}>—</span>;
        }
        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    minWidth: 0,
                    width: '100%',
                }}
            >
                {first ? (
                    <a
                        href={
                            first.permalink ||
                            `https://www.mercadolivre.com.br/anuncios?search=${encodeURIComponent(first.mlb)}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            fontSize: 12,
                            color: T.accent,
                            textDecoration: 'none',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textTransform: 'uppercase',
                            flex: 1,
                            minWidth: 0,
                        }}
                        title={title || first.mlb}
                    >
                        {title || first.mlb}
                    </a>
                ) : (
                    <span style={{ color: T.subtle }}>—</span>
                )}
                {extra > 0 && (
                    <MlbListingsPopover listings={listings} anchor={`+${extra}`} buttonStyle={{ fontSize: 10 }} />
                )}
            </span>
        );
    }

    if (col === 'tags') {
        if (!row.tags?.length) {
            return <span style={{ color: T.subtle }}>—</span>;
        }
        // TODAS as tags como badge, em linha (row tem altura fixa → sem wrap).
        // O que não couber é cortado pelo overflow; tooltip lista tudo e a
        // coluna é redimensionável. Antes mostrava só a 1ª + "+N tags", e o
        // usuário não enxergava as tags do Full sem abrir o tooltip.
        return (
            <div
                style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', overflow: 'hidden', alignItems: 'center' }}
                title={row.tags.join(', ')}
            >
                {row.tags.map((tag) => (
                    <TBadge key={tag} color={getTagBadgeColor(tag)}>
                        {tag}
                    </TBadge>
                ))}
            </div>
        );
    }

    if (col === 'fullAnalysis') {
        // Tags do Envio Full em coluna própria (row.fullTags), separadas das
        // tags gerais a pedido do dono. Mesmo render da coluna Tags.
        const list = Array.isArray(row.fullTags) ? row.fullTags : [];
        if (list.length === 0) {
            return <span style={{ color: T.subtle }}>—</span>;
        }
        return (
            <div
                style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', overflow: 'hidden', alignItems: 'center' }}
                title={list.join(', ')}
            >
                {list.map((tag) => (
                    <TBadge key={tag} color={getTagBadgeColor(tag)}>
                        {tag}
                    </TBadge>
                ))}
            </div>
        );
    }

    if (col === 'trend') {
        const daily = row.daily || [];
        if (daily.length < 2) {
            return <span style={{ color: T.subtle, fontSize: 11 }}>—</span>;
        }
        const max = Math.max(...daily, 0);
        return (
            <div style={{ display: 'flex', alignItems: 'center', paddingTop: 2 }}>
                {/* Cor consistente: cinza zerado, azul com valor — mesma
                    convenção do sparkline das colunas chartable. */}
                <Spark values={daily} width={62} height={18} color={max === 0 ? T.subtle : T.accent} />
            </div>
        );
    }

    if (col === 'bling') {
        // Zero = vermelho via numericCellColor; baixo (sem ser zero) = bold
        // pra chamar atenção sem virar warning crítico.
        const threshold = row.low_stock_threshold ?? 20;
        const v = Number(raw || 0);
        const low = v > 0 && v < threshold;
        return <NumericCell T={T} value={raw ?? 0} weight={low ? 600 : 400} />;
    }

    if (col === 'mlAvail' || col === 'mlUnavail') {
        return <NumericCell T={T} value={raw ?? 0} />;
    }

    if (col === 'coverage') {
        const v = Number(raw || 0);
        if (!Number.isFinite(v)) {
            return <span style={{ color: T.subtle, fontSize: 12 }}>—</span>;
        }
        // Unidade 'd' (dias) renderizada inline pra ficar junto do valor.
        // Cor decidida pelo NÚMERO (não pela string com 'd' anexado).
        return <NumericCell T={T} value={`${v}d`} colorValue={v} />;
    }

    if (col === 'fullCoverage') {
        // null = sem venda recente → sem cobertura calculável ("—"), não zero.
        if (raw === null || raw === undefined || !Number.isFinite(Number(raw))) {
            return <span style={{ color: T.subtle, fontSize: 12 }}>—</span>;
        }
        const v = Number(raw);
        return <NumericCell T={T} value={`${FormatDecimal1(v)}d`} colorValue={v} />;
    }

    if (col === 'fullNeed' || col === 'fullSuggest') {
        // Zero aqui é o estado BOM (nada a enviar) — não usa o vermelho de "zerado".
        const v = Number(raw || 0);
        if (v === 0) {
            return <span style={{ fontFamily: T.mono, fontSize: 12, color: T.subtle }}>0</span>;
        }
        return <NumericCell T={T} value={v} weight={col === 'fullSuggest' ? 600 : 400} />;
    }

    if (
        col === 'forecast' ||
        col === 'stockTotal' ||
        col === 'sales3' ||
        col === 'sales5' ||
        col === 'sales7' ||
        col === 'sales30' ||
        col === 'sales_today' ||
        col === 'sales_yday' ||
        col === 'avgPeriod' ||
        col === 'week1' ||
        col === 'week2' ||
        col === 'week3' ||
        col === 'week4' ||
        col === 'month1' ||
        col === 'month2' ||
        col === 'month3' ||
        col === 'mlTotal'
    ) {
        // 3 modos pra colunas chartable: text (default) | line (sparkline) | bar.
        // Não chartable (forecast, stockTotal, sales_today, etc) sempre texto.
        if (CHARTABLE_COLS.has(col)) {
            const view = columnViews?.[col];
            if (view === 'line') {
                const values = dailySliceForCol(row, col);
                if (values.length < 2) {
                    return <span style={{ color: T.subtle, fontSize: 11 }}>—</span>;
                }
                const max = Math.max(...values, 0);
                // Cor consistente: cinza quando série toda zerada, azul (accent)
                // quando há QUALQUER valor > 0 — sem mais variação por "último
                // ponto" (causava sparklines aparentemente iguais com cores
                // diferentes só porque o último ponto estava em zero).
                const color = max === 0 ? T.subtle : T.accent;
                return (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 0,
                            width: '100%',
                        }}
                    >
                        {/* width 100px — tamanho fixo solicitado, equilibra clareza
                            visual da tendência com espaço pra número à direita. */}
                        <Spark values={values} width={100} height={16} color={color} />
                        <SubText T={T} ml={0}>
                            {raw ?? 0}
                        </SubText>
                    </div>
                );
            }
            if (view === 'bar') {
                return <ChartBar value={raw} max={colMax?.get(col) || 0} T={T} />;
            }
        }
        // avgPeriod retorna number cru (sort numérico); formata 1 decimal aqui.
        if (col === 'avgPeriod') {
            const n = Number(raw || 0);
            return <NumericCell T={T} value={n.toFixed(1)} colorValue={n} />;
        }
        return <NumericCell T={T} value={raw ?? 0} />;
    }

    if (col === 'bestDay') {
        // Valor + data já pre-computados pelo Selector (row.bestDay + row.bestDayDate).
        // Sem recalcular aqui — o Selector é a fonte da verdade.
        const qty = Number(row.bestDay || 0);
        if (qty <= 0 || !row.bestDayDate) {
            return <span style={{ color: T.subtle, fontSize: 12 }}>—</span>;
        }
        const parts = String(row.bestDayDate).split('-');
        const suffix = parts.length === 3 ? `${parts[2]}/${parts[1]}` : '';
        return <NumericCell T={T} value={qty} suffix={suffix} />;
    }

    if (col === 'price' || col === 'cost' || col === 'margin') {
        // Monetários: "R$ X,YZ" exibe a string formatada, cor decidida pelo
        // número parseado (zero → vermelho).
        const numeric = Number(
            String(raw ?? '')
                .replace(/^R\$\s*/, '')
                .replace(/%$/, '')
                .replace(/\./g, '')
                .replace(',', '.'),
        );
        return <NumericCell T={T} value={raw} colorValue={numeric} />;
    }

    return <span style={{ fontSize: 12, color: T.inkMid }}>{raw ?? '—'}</span>;
}

function HeaderRow({
    cols,
    gridTemplate,
    sortCol,
    sortDir,
    onSort,
    colDefs = COL_DEFS,
    periodDays,
    onResizeColumn,
    onAutofit,
    onAutofitAll,
    widthOverrides,
    editing,
    onReorderColumns,
}) {
    const T = useTokens();
    // Estado de drag nativo (HTML5). Só ativo quando `editing` é true.
    const [dragCol, setDragCol] = useState(null);
    const [dropTarget, setDropTarget] = useState(null);

    const handleDragStart = (col) => (e) => {
        if (!editing) return;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', col);
        setDragCol(col);
    };
    const handleDragOver = (col) => (e) => {
        if (!editing || !dragCol || col === dragCol) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dropTarget !== col) setDropTarget(col);
    };
    const handleDrop = (col) => (e) => {
        if (!editing || !dragCol || col === dragCol) {
            setDragCol(null);
            setDropTarget(null);
            return;
        }
        e.preventDefault();
        const from = cols.indexOf(dragCol);
        const to = cols.indexOf(col);
        if (from < 0 || to < 0) {
            setDragCol(null);
            setDropTarget(null);
            return;
        }
        const next = [...cols];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        onReorderColumns?.(next);
        setDragCol(null);
        setDropTarget(null);
    };
    const handleDragEnd = () => {
        setDragCol(null);
        setDropTarget(null);
    };
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: gridTemplate,
                background: T.bgSoft,
                borderBottom: `1px solid ${T.lineMid}`,
                position: 'sticky',
                top: 0,
                zIndex: 5,
                flexShrink: 0,
                // width:max-content + minWidth:100% — quando a soma das colunas
                // ultrapassa o container, o header se expande junto com as rows
                // (mantendo bg contínuo durante scroll horizontal). Quando cabe,
                // ainda preenche 100% pra evitar bg cortado.
                width: 'max-content',
                minWidth: '100%',
            }}
        >
            {cols.map((col, i) => {
                const def = colDefs[col] || COL_DEFS[col] || { label: col, align: 'left' };
                const isLast = i === cols.length - 1;
                if (col === 'select') {
                    // Header da coluna do carrinho: ícone ShoppingCart visível +
                    // botão de autofit-all em dblclick (estilo Excel "selecionar
                    // canto superior esquerdo"). Tem ResizeHandle dedicado na
                    // borda direita pra resize manual da própria coluna 'select'.
                    const selectWidthPx = Number.isFinite(widthOverrides?.select)
                        ? widthOverrides.select
                        : parseInt(String(def.w || '56px'), 10) || 56;
                    return (
                        <div
                            key={col}
                            style={{
                                position: 'relative',
                                display: 'flex',
                                borderRight: `1px solid ${T.line}`,
                            }}
                        >
                            <button
                                type="button"
                                title="Duplo clique: ajustar largura de todas as colunas"
                                onDoubleClick={() => {
                                    onAutofitAll?.();
                                }}
                                style={{
                                    flex: 1,
                                    padding: '0 8px',
                                    height: 36,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: T.muted,
                                    outline: 'none',
                                }}
                            >
                                <ShoppingCart size={14} />
                            </button>
                            {/* ResizeHandle dedicado pra coluna 'select' — drag
                                pra redimensionar; dblclick na zona do handle =
                                autofit do próprio header (label vazio = encolhe
                                pro mínimo). */}
                            <ResizeHandle
                                initialWidth={selectWidthPx}
                                onResize={(px) => onResizeColumn?.('select', px)}
                                onAutofit={() => onAutofit?.('select')}
                            />
                        </div>
                    );
                }
                const isSorted = sortCol === col;
                const masked = isColumnMasked(col, periodDays);
                // Largura atual em px (pra passar pro ResizeHandle como `initialWidth`).
                const currentWidthPx = Number.isFinite(widthOverrides?.[col])
                    ? widthOverrides[col]
                    : parseInt(String(def.w || '80px'), 10) || 80;
                const isDragSource = editing && dragCol === col;
                const isDropTarget = editing && dropTarget === col;
                return (
                    // biome-ignore lint/a11y/noStaticElementInteractions: drag-drop é progressive enhancement no header — o <button> filho mantém click/sort acessível por teclado
                    <div
                        key={col}
                        draggable={editing}
                        onDragStart={handleDragStart(col)}
                        onDragOver={handleDragOver(col)}
                        onDrop={handleDrop(col)}
                        onDragEnd={handleDragEnd}
                        style={{
                            position: 'relative',
                            display: 'flex',
                            minWidth: 0,
                            borderRight: isLast ? 'none' : `1px solid ${T.line}`,
                            background: isDropTarget ? T.accentBg : isSorted && !masked ? T.bgMid : 'transparent',
                            opacity: isDragSource ? 0.4 : masked ? 0.5 : 1,
                            boxShadow: isDropTarget ? `inset 2px 0 0 0 ${T.accent}` : 'none',
                            cursor: editing ? 'grab' : 'default',
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => !masked && !editing && onSort(col)}
                            // Desabilita o botão em edit pra não competir com o drag do pai;
                            // remove pointer-events:none que conflitava com a semântica de button.
                            disabled={masked || editing}
                            // Hover sobre o header da TABELA: mostra `name` (forma longa,
                            // ex: "Mercado Livre disponível") + range de datas que a coluna
                            // representa (ex: "01/12/2025 à 22/05/2026"). Schema sem fallbacks
                            // — `name` sempre existe.
                            title={(() => {
                                if (editing) return 'Arraste pra reordenar';
                                if (masked) return 'Indisponível para o período filtrado';
                                const longTitle = def?.name || col;
                                const range = getColumnDateRangeText(col, { periodDays });
                                return range ? `${longTitle} · ${range}` : longTitle;
                            })()}
                            style={{
                                flex: 1,
                                minWidth: 0,
                                padding: '0 10px',
                                height: 36,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: def.align === 'right' ? 'flex-end' : 'flex-start',
                                gap: 5,
                                cursor: editing ? 'grab' : masked ? 'not-allowed' : 'pointer',
                                userSelect: 'none',
                                border: 'none',
                                color: masked ? T.subtle : isSorted ? T.ink : T.muted,
                                background: 'transparent',
                                outline: 'none',
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    minWidth: 0,
                                }}
                            >
                                {def.label}
                            </span>
                            {/* Indicador de sort renderizado APENAS quando ordenado.
                            Não há slot reservado — quando uma coluna entra em sort
                            o `buildGridTemplate(sortCol)` mede o label e expande a
                            largura SE precisar (resolveColumnWidth faz a conta). */}
                            {isSorted && !masked && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>
                                    {sortDir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
                                </span>
                            )}
                        </button>
                        {/* ResizeHandle no canto direito de TODAS as colunas exceto a última.
                        Drag horizontal redimensiona; duplo clique = autofit (zona
                        determinada por clientY: dentro do header = autofit do label,
                        nas linhas = autofit do conteúdo). */}
                        {/* ResizeHandle TAMBÉM na última coluna — sem ele, o user
                        não conseguia ajustar a largura da coluna mais à direita.
                        Antes o `!isLast` escondia o handle nela. Para que o
                        resize funcione, o pai (grid row) precisa usar width:
                        max-content + minWidth: 100% — o que já fizemos. */}
                        <ResizeHandle
                            initialWidth={currentWidthPx}
                            onResize={(px) => onResizeColumn?.(col, px)}
                            onAutofit={() => onAutofit?.(col)}
                        />
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Resolve refs `custom:<id>` no preset → mapa <refKey, def>, onde refKey =
 * `custom:<id>` (matching o que aparece em preset.columns e o que `renderCell`
 * recebe). Definições vêm do `customColumnsById` (global, useCustomColumns).
 *
 * Aceita também forma legada `{id, type:'custom', name, expression, format}`
 * inline pra qualquer preset que ainda não foi migrado (a migration cuida disso
 * no backend mas defensivo aqui evita render quebrado durante deploy).
 */
function useCustomColDefs(preset, customColumnsById) {
    return useMemo(() => {
        const map = new Map();
        if (!Array.isArray(preset?.columns)) return map;
        for (const col of preset.columns) {
            // Ref global: "custom:<id>"
            if (typeof col === 'string' && col.startsWith('custom:')) {
                const id = col.slice('custom:'.length);
                const def = customColumnsById?.get(id);
                if (def) {
                    map.set(col, def);
                }
                continue;
            }
            // Legado inline (não esperado pós-migration mas defensivo)
            if (col && typeof col === 'object' && col.type === 'custom') {
                map.set(col.id, col);
            }
        }
        return map;
    }, [preset?.columns, customColumnsById]);
}

function useMergedColDefs(customColDefs) {
    return useMemo(() => {
        if (customColDefs.size === 0) return COL_DEFS;
        const merged = { ...COL_DEFS };
        customColDefs.forEach((def, id) => {
            // Convenção do schema (data.js COL_DEFS): `label` = curto pro header,
            // `name` = longo pra lista/picker. Custom col: short_label opcional;
            // se ausente, header usa o próprio name (caso degenerado, deve ser raro).
            merged[id] = {
                label: def.short_label || def.name,
                name: def.name,
                w: def.width || '100px',
                align: 'right',
                mono: true,
            };
        });
        return merged;
    }, [customColDefs]);
}

function useAugmentedRows(rows, customColDefs) {
    return useMemo(() => {
        if (customColDefs.size === 0) return rows;
        return rows.map((row) => {
            const extras = {};
            customColDefs.forEach((def, id) => {
                try {
                    extras[id] = EvaluateExpression(def.expression, row);
                } catch (e) {
                    extras[id] = e;
                }
            });
            return { ...row, ...extras };
        });
    }, [rows, customColDefs]);
}

function normalizePresetCols(preset) {
    // Defensivo: preset pode ser null em estados transitórios (boot, delete do
    // último preset). Sem preset = tabela renderiza sem colunas e o caller
    // tipicamente mostra um empty state.
    if (!preset?.columns) return [];
    return preset.columns.map((c) => (typeof c === 'string' ? c : c?.id)).filter(Boolean);
}

/**
 * Calcula o máximo por coluna chartable no conjunto de linhas atual. Só varre
 * as colunas que estão em modo 'bar' — evita scan desnecessário pra colunas em
 * modo texto. Devolve um Map<colId, number>.
 */
function toNumericValue(raw) {
    if (raw === null || raw === undefined || raw === '') return null;
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
    // Strings podem vir formatadas ("R$ 12,34", "45%") — strip prefixos/sufixos
    // e converte vírgula → ponto antes de parsear.
    const cleaned = String(raw)
        .replace(/^R\$\s*/, '')
        .replace(/%$/, '')
        .replace(/d$/, '') // "30d" da coverage
        .replace(/\./g, '')
        .replace(',', '.')
        .trim();
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
}

function computeChartableColMax(columnViews, rows, customColDefs = new Map()) {
    const map = new Map();
    if (!columnViews || !rows || rows.length === 0) return map;
    for (const [col, view] of Object.entries(columnViews)) {
        if (view !== 'bar') continue;
        // Custom cols têm valor pré-computado em row[col] (useAugmentedRows).
        // Built-in cols → getCellValue (resolve stockTotal/bestDay etc).
        const isCustom = customColDefs.has(col);
        let max = 0;
        for (const r of rows) {
            const raw = isCustom ? r[col] : getCellValue(r, col);
            const v = toNumericValue(raw);
            if (v !== null && v > max) max = v;
        }
        map.set(col, max);
    }
    return map;
}

// === Subtotais por coluna (linha sticky no rodapé da tabela) ================

/**
 * Valor NUMÉRICO de uma célula — mesma resolução do renderCell: custom col já
 * vem pré-computada em row[col] (useAugmentedRows); built-in resolve via
 * getCellValue. toNumericValue normaliza "R$ 12,34" / "45%" / "30d" → number.
 * Retorna number ou null (texto/erro/vazio).
 */
function cellNumber(row, col, periodDays, customColDefs) {
    const isCustom = customColDefs?.has(col);
    const raw = isCustom ? row[col] : getCellValue(row, col, { periodDays });
    return toNumericValue(raw);
}

// Colunas de TAXA / percentual / monetário → subtotal faz MÉDIA (somar % ou
// preço unitário não faz sentido). As demais numéricas SOMAM.
const SUBTOTAL_PERCENT_COLS = new Set(['margin', 'weekGrowth', 'monthVariation', 'periodSplit', 'cancelPercent']);
const SUBTOTAL_AVG_COLS = new Set(['avgPeriod', 'coverage', 'fullCoverage', 'price', 'cost', ...SUBTOTAL_PERCENT_COLS]);

/**
 * Agrega uma coluna sobre as rows informadas (a PÁGINA atual). Retorna
 * `{ op:'sum'|'avg', value, count }` ou null quando a coluna não agrega (texto,
 * masked pelo período, ou sem nenhum valor numérico).
 */
function subtotalForColumn(col, rows, periodDays, customColDefs) {
    if (col === 'select' || col === 'trend') return null;
    const customDef = customColDefs?.get(col) || null;
    if (!isNumericColumn(col, customDef)) return null;
    if (isColumnMasked(col, periodDays)) return null; // período insuficiente → célula "—"

    let sum = 0;
    let count = 0;
    for (const r of rows) {
        const v = cellNumber(r, col, periodDays, customColDefs);
        if (v === null || Number.isNaN(v)) continue;
        sum += v;
        count += 1;
    }
    if (count === 0) return null;

    let op = 'sum';
    if (customDef) op = customDef.format === 'percentage' ? 'avg' : 'sum';
    else if (SUBTOTAL_AVG_COLS.has(col)) op = 'avg';

    return { op, value: op === 'avg' ? sum / count : sum, count };
}

/** Formata o subtotal respeitando a unidade da coluna. */
function formatSubtotal(col, agg, customDef) {
    const { value } = agg;
    if (customDef) {
        if (customDef.format === 'percentage') return FormatPercent(value);
        if (customDef.format === 'currency') return FormatPrice(value);
        return Number.isInteger(value) ? FormatInt(value) : FormatDecimal1(value);
    }
    if (SUBTOTAL_PERCENT_COLS.has(col)) return FormatPercent(value);
    if (col === 'price' || col === 'cost') return FormatPrice(value);
    if (col === 'coverage' || col === 'fullCoverage') return `${FormatDecimal1(value)}d`;
    if (col === 'avgPeriod') return FormatDecimal1(value);
    return Number.isInteger(value) ? FormatInt(value) : FormatDecimal1(value);
}

/**
 * Linha de SUBTOTAIS — sticky no fundo do container de scroll (logo acima do
 * footer de paginação). Recalcula sobre a PÁGINA atual: ao paginar, os
 * subtotais mudam pra refletir as rows visíveis. `position:sticky; bottom:0`
 * dá o comportamento pedido: quando há scroll a linha PERMANECE colada no
 * rodapé do viewport; quando cabe sem scroll, ela aparece após a última row.
 *
 * Σ no primeiro cell marca a linha; cada coluna numérica mostra Soma (aditivas)
 * ou Média (taxas/%/monetárias), na mesma unidade da coluna.
 */
function SubtotalRow({ cols, gridTemplate, rows, periodDays, customColDefs, mergedColDefs, T }) {
    const aggs = useMemo(
        () => cols.map((col) => subtotalForColumn(col, rows, periodDays, customColDefs)),
        [cols, rows, periodDays, customColDefs],
    );
    if (!aggs.some(Boolean)) return null; // nenhuma coluna agregável neste preset

    return (
        <div
            data-faid="Data_Table_Subtotal_Operacoes"
            style={{
                display: 'grid',
                gridTemplateColumns: gridTemplate,
                position: 'sticky',
                bottom: 0,
                zIndex: 4,
                background: T.bgMid,
                borderTop: `2px solid ${T.lineMid}`,
                width: 'max-content',
                minWidth: '100%',
                height: 34,
                boxShadow: `0 -2px 6px ${T.line}`,
            }}
        >
            {cols.map((col, ci) => {
                const def = mergedColDefs[col] || { align: 'left' };
                const agg = aggs[ci];
                const text = agg ? formatSubtotal(col, agg, customColDefs?.get(col) || null) : '';
                return (
                    <div
                        key={col}
                        style={{
                            padding: '0 10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: def.align === 'right' ? 'flex-end' : 'flex-start',
                            gap: 6,
                            borderRight: ci < cols.length - 1 ? `1px solid ${T.line}` : 'none',
                            overflow: 'hidden',
                        }}
                    >
                        {ci === 0 && (
                            <span
                                title="Subtotal da página atual"
                                style={{
                                    fontFamily: T.sans,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: T.accent,
                                    flexShrink: 0,
                                }}
                            >
                                Σ
                            </span>
                        )}
                        {text && (
                            <span
                                title={`${agg.op === 'avg' ? 'Média' : 'Soma'} · ${agg.count} ${agg.count === 1 ? 'item' : 'itens'}`}
                                style={{
                                    fontFamily: T.mono,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: T.ink,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {text}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Constrói `grid-template-columns` envolvendo fr units em `minmax(0, ...)`.
 * Sem isso `1.8fr` vira `minmax(auto, 1.8fr)` e cresce com o conteúdo intrínseco
 * da célula. Cada linha resolve seu grid de forma independente — quando o espaço
 * fica apertado (sidebar aberto), header e linhas calculam larguras diferentes
 * e desalinham. `minmax(0, fr)` força a coluna a respeitar estritamente a fração.
 */
function buildGridTemplate(cols, colDefs, widthOverrides = {}, sortCol = null) {
    return cols
        .map((c) => {
            // Passa `isSorted` pro resolver — ele mede o label e cresce só se o
            // espaço atual for insuficiente pra caber label + ícone de sort.
            const w = ResolveColumnWidth(c, colDefs, widthOverrides, c === sortCol);
            if (typeof w === 'string' && /fr$/.test(w.trim())) {
                return `minmax(0, ${w})`;
            }
            return w;
        })
        .join(' ');
}

/**
 * Hook que junta useColumnWidths + handlers de autofit. Compartilhado por
 * DataTable e DataTableInline.
 */
function useResizeIntegration({ presetId, mergedColDefs, visibleRows, columnViews, cols = [], cartBySku = null }) {
    const { widthOverrides, setWidth, setWidths, resetColumn } = useColumnWidths(presetId);
    const onResizeColumn = useCallback((col, px) => setWidth(col, px), [setWidth]);
    // Duplo clique no header = autofit ÚNICO: max(header_width, content_width)
    // limitado a AUTO_WIDTH_MAX_PX (160). Pra ir além de 160px, user arrasta.
    const onAutofit = useCallback(
        (col) => {
            // Coluna 'select' (carrinho): base 40px (altura da row, fica
            // quadrada vazio) mas cresce se algum input tem mais que 2 dígitos
            // — mesma lógica do onAutofitAll. Antes esta branch só fazia
            // `setWidth(col, baseW)` ignorando cartBySku → dblclick na borda
            // voltava sempre pra 40px, mesmo com qty 209000 no carrinho.
            if (col === 'select') {
                const baseW = parseInt(String(mergedColDefs.select?.w || '40px'), 10) || 40;
                let maxDigitsW = 0;
                if (cartBySku) {
                    for (const row of visibleRows) {
                        const sku = String(row?.sku || '').toUpperCase();
                        const qty = Number(cartBySku.get(sku)?.qty || 0);
                        if (qty > 0) {
                            const w = String(qty).length * 9 + 20;
                            if (w > maxDigitsW) maxDigitsW = w;
                        }
                    }
                }
                setWidth(col, Math.min(AUTO_WIDTH_MAX_PX, Math.max(baseW, maxDigitsW)));
                return;
            }
            const view = columnViews?.[col] || null;
            const headerW = ComputeAutofitForHeader(col, mergedColDefs, HEADER_FONT);
            const contentW = ComputeAutofitForContent(col, visibleRows, CELL_FONT, view);
            setWidth(col, Math.min(AUTO_WIDTH_MAX_PX, Math.max(headerW, contentW)));
        },
        [mergedColDefs, visibleRows, setWidth, columnViews, cartBySku],
    );

    /**
     * Autofit em TODAS as colunas. Computa o ajuste de cada uma e aplica num
     * único setWidths (batch) — sem isso o setWidth em loop sofria stale closure
     * e só a última coluna persistia.
     *
     * Coluna 'select' (carrinho): respeita o `w` declarado em COL_DEFS (40px
     * default = altura da row, fica quadrada). Cresce só se algum valor no
     * input tiver mais que 2 dígitos.
     */
    const onAutofitAll = useCallback(() => {
        const updates = {};
        for (const col of cols) {
            const view = columnViews?.[col] || null;
            if (col === 'select') {
                // Pra 'select': base 40 (definido em COL_DEFS.select.w). Cresce
                // somente se algum input no carrinho tiver muitos dígitos —
                // cartBySku é Map<sku, {qty}>; antes lia row.cartQty/qty que
                // NÃO EXISTEM nas rows do operacoes, então o autofit nunca
                // crescia mesmo com qty 209000.
                const baseW = parseInt(String(mergedColDefs.select?.w || '40px'), 10) || 40;
                let maxDigitsW = 0;
                if (cartBySku) {
                    for (const row of visibleRows) {
                        const sku = String(row?.sku || '').toUpperCase();
                        const qty = Number(cartBySku.get(sku)?.qty || 0);
                        if (qty > 0) {
                            // ~9px por dígito mono + 20px padding interno
                            const w = String(qty).length * 9 + 20;
                            if (w > maxDigitsW) maxDigitsW = w;
                        }
                    }
                }
                updates.select = Math.min(AUTO_WIDTH_MAX_PX, Math.max(baseW, maxDigitsW));
                continue;
            }
            const headerW = ComputeAutofitForHeader(col, mergedColDefs, HEADER_FONT);
            const contentW = ComputeAutofitForContent(col, visibleRows, CELL_FONT, view);
            updates[col] = Math.min(AUTO_WIDTH_MAX_PX, Math.max(headerW, contentW));
        }
        setWidths(updates);
    }, [cols, mergedColDefs, visibleRows, columnViews, setWidths, cartBySku]);
    return { widthOverrides, onResizeColumn, onAutofit, onAutofitAll, resetColumn };
}

const PAGE_SIZE_OPTS = [25, 50, 100, 200, 500];
const DEFAULT_PAGE_SIZE = 50;

function TableFooter({ total, page, pageSize, periodOrderCount, onPageChange, onPageSizeChange }) {
    const T = useTokens();
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(Math.max(1, page), totalPages);
    const fromIdx = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const toIdx = Math.min(safePage * pageSize, total);

    const goPrev = () => onPageChange(Math.max(1, safePage - 1));
    const goNext = () => onPageChange(Math.min(totalPages, safePage + 1));

    // Botões nav usam SÓ ícone (‹ / ›) — texto "Anterior/Próxima" quebrava
    // linha em containers estreitos (~460px). Pageinfo "N / total" com
    // whiteSpace:nowrap pra não quebrar. flex-shrink:0 em cada elemento
    // garante que não somem com Flexbox.
    const navBtnStyle = (disabled) => ({
        padding: '3px 8px',
        minWidth: 28,
        height: 26,
        borderRadius: 4,
        border: `1px solid ${T.line}`,
        background: T.bg,
        color: T.ink,
        fontSize: 13,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        outline: 'none',
        flexShrink: 0,
        lineHeight: 1,
        fontFamily: T.mono,
    });
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '0 12px',
                height: T.footerH,
                borderTop: `1px solid ${T.line}`,
                background: T.bgSoft,
                fontSize: 12,
                color: T.muted,
                fontFamily: T.sans,
                flexShrink: 0,
                // overflow-x scroll defensivo: em width minúsculo (~360px),
                // ainda permite arrastar pra ver tudo sem quebrar verticalmente.
                overflowX: 'auto',
                whiteSpace: 'nowrap',
            }}
        >
            <span style={{ fontFamily: T.mono, flexShrink: 0 }}>
                {fromIdx.toLocaleString('pt-BR')}–{toIdx.toLocaleString('pt-BR')}{' '}
                <span style={{ color: T.subtle }}>de</span>{' '}
                <b style={{ color: T.ink }}>{total.toLocaleString('pt-BR')}</b>
                {Number.isFinite(periodOrderCount) && (
                    <>
                        {' · '}
                        <b style={{ color: T.ink }}>{Number(periodOrderCount).toLocaleString('pt-BR')}</b> pedidos
                    </>
                )}
            </span>
            <div style={{ flex: 1, minWidth: 8 }} />
            <select
                value={pageSize}
                data-faid="Page_Size_Select_Operacoes"
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                title="Linhas por página"
                style={{
                    padding: '3px 6px',
                    borderRadius: 4,
                    border: `1px solid ${T.line}`,
                    background: T.bg,
                    color: T.ink,
                    fontSize: 12,
                    fontFamily: T.sans,
                    cursor: 'pointer',
                    outline: 'none',
                    flexShrink: 0,
                }}
            >
                {PAGE_SIZE_OPTS.map((n) => (
                    <option key={n} value={n}>
                        {n} linhas
                    </option>
                ))}
            </select>
            <button
                type="button"
                onClick={goPrev}
                disabled={safePage <= 1}
                title="Página anterior"
                style={navBtnStyle(safePage <= 1)}
            >
                ‹
            </button>
            <span style={{ fontFamily: T.mono, color: T.ink, flexShrink: 0, whiteSpace: 'nowrap' }}>
                {safePage}/{totalPages}
            </span>
            <button
                type="button"
                onClick={goNext}
                disabled={safePage >= totalPages}
                title="Próxima página"
                style={navBtnStyle(safePage >= totalPages)}
            >
                ›
            </button>
        </div>
    );
}

export function DataTable({
    preset,
    rows,
    selectedId,
    onSelect,
    sortCol,
    sortDir,
    onSort,
    compact,
    periodDays,
    cartBySku,
    onSetCartQty,
    editing,
    onReorderColumns,
    customColumnsById,
}) {
    const T = useTokens();
    const customColDefs = useCustomColDefs(preset, customColumnsById);
    const mergedColDefs = useMergedColDefs(customColDefs);
    // Rows já vêm ordenadas do index.jsx (para nav <> seguir ordem visual).
    const augmentedRows = useAugmentedRows(rows, customColDefs);
    const cols = useMemo(() => normalizePresetCols(preset), [preset]);
    // T.rowH = 40px (padronizado em tokens.js). Compact = 36 mantido pra usos
    // off-painel principal que ainda invocam `compact`.
    const rowH = compact ? 36 : T.rowH;
    const tableContainerRef = useRef(null);

    // Hoist columnViews antes do useResizeIntegration pra passar pro autofit
    // (que precisa saber se a coluna está em modo gráfico pra dimensionar certo).
    const columnViews = useMemo(() => preset?.columnViews || {}, [preset?.columnViews]);

    // Paginação calculada ANTES do useResizeIntegration porque o autofit usa
    // SÓ os rows visíveis (página atual) — paginar não deve disparar resize,
    // mas dblclick na borda usa o que está em tela.
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const resetKey = `${preset?.id || ''}|${sortCol || ''}|${sortDir || ''}|${pageSize}|${augmentedRows.length}`;
    const [pageState, setPageState] = useState({ key: resetKey, page: 1 });
    let page = pageState.page;
    if (pageState.key !== resetKey) {
        page = 1;
        setPageState({ key: resetKey, page: 1 });
    }
    const setPage = (next) => setPageState({ key: resetKey, page: next });
    const total = augmentedRows.length;
    const safePage = Math.min(Math.max(1, page), Math.max(1, Math.ceil(total / pageSize)));
    const pageRows = augmentedRows.slice((safePage - 1) * pageSize, safePage * pageSize);

    // Total de PEDIDOS (distinct order ids) no PERÍODO ATIVO sobre as rows
    // FILTRADAS. row.orderCount = orderTotals.paid+cancelled+other, JÁ
    // period-clipped pelo Selector. Soma cross-rows = pedidos que o Analista
    // entregou pro Renderer trabalhar.
    //
    // Nota: pode ter dupla-contagem se o MESMO pedido tem itens de SKUs
    // diferentes visíveis (raro em prática). Cálculo distinct cross-SKU
    // exigiria expor order_ids no snapshot — overhead grande, fica como
    // melhoria futura se a precisão importar.
    const periodOrderCount = useMemo(
        () => augmentedRows.reduce((s, r) => s + Number(r?.orderCount || 0), 0),
        [augmentedRows],
    );

    const { widthOverrides, onResizeColumn, onAutofit, onAutofitAll } = useResizeIntegration({
        presetId: preset?.id,
        mergedColDefs,
        visibleRows: pageRows,
        columnViews,
        cols,
        cartBySku,
    });

    // Auto-autofit ao trocar de preset: equivale ao dblclick global do
    // resize. Sem isso, larguras herdadas de outro preset ficam erradas
    // (colunas diferentes têm conteúdo de tamanhos diferentes). Aciona só
    // QUANDO o preset.id mudar E houver linhas pra medir.
    const prevPresetIdRef = useRef(preset?.id);
    useEffect(() => {
        if (prevPresetIdRef.current !== preset?.id && pageRows.length > 0) {
            prevPresetIdRef.current = preset?.id;
            onAutofitAll();
        }
    }, [preset?.id, pageRows.length, onAutofitAll]);

    // Larguras padrão CALCULADAS por coluna — usado quando o user ainda não
    // customizou (widthOverrides[col] vazio). Regra: max(header, valor mais
    // largo na PÁGINA atual), com cap em 160px pra coluna não nascer enorme.
    // User pode dar dblclick na borda OU drag pra ultrapassar 160 manualmente.
    // Memo recalculado quando muda preset/página/columnViews — não a cada render.
    const autoWidths = useMemo(() => {
        const map = {};
        for (const col of cols) {
            if (col === 'select') continue;
            const headerW = ComputeAutofitForHeader(col, mergedColDefs, HEADER_FONT);
            const contentW = ComputeAutofitForContent(col, pageRows, CELL_FONT, columnViews?.[col]);
            map[col] = Math.min(160, Math.max(headerW, contentW));
        }
        return map;
    }, [cols, mergedColDefs, pageRows, columnViews]);

    // Merge: widthOverrides (user) sobre autoWidths (system defaults) sobre
    // COL_DEFS.w (estático). buildGridTemplate vai resolver na ordem certa.
    const effectiveOverrides = useMemo(() => ({ ...autoWidths, ...widthOverrides }), [autoWidths, widthOverrides]);
    const gridTemplate = buildGridTemplate(cols, mergedColDefs, effectiveOverrides, sortCol);

    // colMax depende do pageRows atual (proporcional ao que está visível).
    const colMax = useMemo(
        () => computeChartableColMax(columnViews, pageRows, customColDefs),
        [columnViews, pageRows, customColDefs],
    );

    // === Seleção de células estilo Excel (click+arrasta → soma/média/...) ====
    // Flags numéricas por índice de coluna (numérica E não-masked pelo período).
    const numericColFlags = useMemo(
        () => cols.map((col) => isNumericColumn(col, customColDefs.get(col)) && !isColumnMasked(col, periodDays)),
        [cols, customColDefs, periodDays],
    );
    const isColNumeric = useCallback((ci) => numericColFlags[ci] === true, [numericColFlags]);
    const getCellNumber = useCallback(
        (ri, ci) => {
            const row = pageRows[ri];
            const col = cols[ci];
            if (!row || col == null) return null;
            return cellNumber(row, col, periodDays, customColDefs);
        },
        [pageRows, cols, periodDays, customColDefs],
    );
    const selection = useCellSelection({ containerRef: tableContainerRef, isColNumeric, getCellNumber });
    const { clear: clearSelection, isSelected: isCellSelected, consumeClickSuppression } = selection;

    // Paginar / ordenar / trocar preset / refiltrar reindexar as rows → a
    // seleção (por índice) deixaria de apontar pras células certas; limpa
    // nessas transições. `resetKey` já agrega preset/sort/pageSize/contagem;
    // anexa a página atual. Padrão ref-compare (mesmo do auto-autofit acima)
    // pra "reagir à mudança" sem deps não-usadas.
    const selResetKey = `${resetKey}|${safePage}`;
    const prevSelKeyRef = useRef(selResetKey);
    useEffect(() => {
        if (prevSelKeyRef.current !== selResetKey) {
            prevSelKeyRef.current = selResetKey;
            clearSelection();
        }
    }, [selResetKey, clearSelection]);

    return (
        <div
            data-faid="Data_Table_Container_Operacoes"
            style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
        >
            <div
                ref={tableContainerRef}
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'auto',
                    scrollbarGutter: 'stable',
                    minWidth: 0,
                    // Durante o arrasto de seleção, mata a seleção de texto nativa.
                    userSelect: selection.selecting ? 'none' : undefined,
                }}
            >
                <HeaderRow
                    cols={cols}
                    gridTemplate={gridTemplate}
                    sortCol={sortCol}
                    sortDir={sortDir}
                    onSort={onSort}
                    colDefs={mergedColDefs}
                    periodDays={periodDays}
                    widthOverrides={effectiveOverrides}
                    onResizeColumn={onResizeColumn}
                    onAutofit={onAutofit}
                    onAutofitAll={onAutofitAll}
                    editing={editing}
                    onReorderColumns={onReorderColumns}
                />

                {pageRows.length === 0 && (
                    <div
                        style={{
                            display: 'grid',
                            placeItems: 'center',
                            height: 120,
                            color: T.muted,
                            fontSize: 13,
                        }}
                    >
                        Nenhum resultado
                    </div>
                )}
                {pageRows.map((row, ri) => {
                    // Em modo MLB, row.sku pode ser nulo — usar row.id (MLB) como key/seletor.
                    const rowKey = row.id || row.sku;
                    const selected = rowKey === selectedId;
                    return (
                        <Fragment key={rowKey}>
                            <button
                                type="button"
                                onClick={(e) => {
                                    // Se acabou de haver um arrasto de seleção de
                                    // células, engole o clique (não abre o painel).
                                    if (consumeClickSuppression()) {
                                        e.preventDefault();
                                        return;
                                    }
                                    onSelect(row);
                                }}
                                data-faid="Data_Table_Row_Operacoes"
                                data-row-id={rowKey}
                                data-row-sku={row.sku || ''}
                                data-row-mlb={row.mlb || ''}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: gridTemplate,
                                    background: selected ? T.accentBg : ri % 2 === 0 ? T.bg : T.bgSoft,
                                    cursor: 'pointer',
                                    height: rowH,
                                    width: 'max-content',
                                    minWidth: '100%',
                                    padding: 0,
                                    border: 'none',
                                    borderBottom: `1px solid ${T.line}`,
                                    transition: 'background 0.08s',
                                    outline: 'none',
                                    textAlign: 'left',
                                    font: 'inherit',
                                    boxSizing: 'border-box',
                                }}
                            >
                                {cols.map((col, ci) => {
                                    const def = mergedColDefs[col] || { align: 'left' };
                                    const numeric = numericColFlags[ci] === true;
                                    const isSel = numeric && isCellSelected(ri, ci);
                                    return (
                                        <div
                                            key={col}
                                            data-faid="Data_Table_Cell_Operacoes"
                                            data-col={col}
                                            data-sel-rc={numeric ? `${ri}:${ci}` : undefined}
                                            style={{
                                                padding: '0 10px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: def.align === 'right' ? 'flex-end' : 'flex-start',
                                                borderRight: ci < cols.length - 1 ? `1px solid ${T.line}` : 'none',
                                                overflow: 'hidden',
                                                height: '100%',
                                                boxSizing: 'border-box',
                                                // Seleção estilo Excel: célula numérica é selecionável
                                                // (cursor 'cell') e ganha realce dentro do retângulo.
                                                cursor: numeric ? 'cell' : undefined,
                                                userSelect: numeric ? 'none' : undefined,
                                                background: isSel ? T.accentBg : undefined,
                                                boxShadow: isSel ? `inset 0 0 0 1px ${T.accent}` : undefined,
                                            }}
                                        >
                                            {renderCell(
                                                row,
                                                col,
                                                T,
                                                customColDefs,
                                                periodDays,
                                                cartBySku,
                                                onSetCartQty,
                                                columnViews,
                                                colMax,
                                            )}
                                        </div>
                                    );
                                })}
                            </button>
                        </Fragment>
                    );
                })}

                {/* Linha de subtotais — sticky no fundo do scroll, antes do
                    footer de paginação. Recalcula sobre a página atual. */}
                {pageRows.length > 0 && (
                    <SubtotalRow
                        cols={cols}
                        gridTemplate={gridTemplate}
                        rows={pageRows}
                        periodDays={periodDays}
                        customColDefs={customColDefs}
                        mergedColDefs={mergedColDefs}
                        T={T}
                    />
                )}
            </div>

            <TableFooter
                total={total}
                page={safePage}
                pageSize={pageSize}
                periodOrderCount={periodOrderCount}
                onPageChange={setPage}
                onPageSizeChange={(n) => setPageSize(n)}
            />

            {/* Barra flutuante (portal) com soma/média/mín/máx da seleção. */}
            <SelectionStatsBar visible={selection.barVisible} pos={selection.barPos} stats={selection.stats} T={T} />
        </div>
    );
}
